import React, { useEffect, useState, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Select from "react-select";
import Creatable from "react-select/creatable";
import { addHotel, updateHotel } from "../../api/owner/hotels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import AddressAutocomplete from "../MapPicker";
import { FiTrash } from "react-icons/fi";
import { allCountries } from "country-telephone-data";

function AddHotelModel({ isOpen, onClose, isEditing, hotel }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    rating: "",
    contact: "",
    address: "",
    description: "",
    totalBedrooms: "",
    minPrice: "",
    maxPrice: "",
    washrooms: "",
    facilities: "",
    email: "",
    priceRange: "",
    latitude: "",
    longitude: "",
    extras: "",
    payAtCheckout: false,
    images: [],
    hasRestaurant: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    category: "",
    rating: "",
    contact: "",
    address: "",
    description: "",
    totalBedrooms: "",
    latitude: "",
    longitude: "",
    minPrice: "",
    maxPrice: "",
    washrooms: "",
    facilities: "",
    email: "",
    priceRange: "",
    location: "",
    extras: "",
    payAtCheckout: false,
    images: "",
  });
  const [existingImages, setExistingImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const [contactCountryCode, setContactCountryCode] = useState("995");
  const [contactLocalNumber, setContactLocalNumber] = useState("");

  // Reset form when modal opens for new hotel
  useEffect(() => {
    if (isOpen && !isEditing && !hotel) {
      // Clean up existing preview URLs
      setImagePreviewUrls((prev) => {
        prev.forEach(url => {
          if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
        return [];
      });
      setExistingImages([]);
      setEnabled(false);
      setFormData({
        name: "",
        category: "",
        rating: "",
        contact: "",
        address: "",
        description: "",
        totalBedrooms: "",
        minPrice: "",
        maxPrice: "",
        washrooms: "",
        facilities: "",
        email: "",
        priceRange: "",
        latitude: "",
        longitude: "",
        extras: "",
        payAtCheckout: false,
        images: [],
        hasRestaurant: "",
      });
      setErrors({
        name: "",
        category: "",
        rating: "",
        contact: "",
        address: "",
        description: "",
        totalBedrooms: "",
        latitude: "",
        longitude: "",
        minPrice: "",
        maxPrice: "",
        washrooms: "",
        facilities: "",
        email: "",
        priceRange: "",
        location: "",
        extras: "",
        payAtCheckout: false,
        images: "",
      });
      setContactCountryCode("995");
      setContactLocalNumber("");
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, isEditing, hotel]);

  // Load hotel data when editing
  useEffect(() => {
    if (isEditing && hotel) {
      setFormData({
        name: hotel.name || "",
        category: hotel.category || "",
        rating: hotel.rating || "",
        contact: hotel.contact || "",
        address: hotel.address || "",
        description: hotel.description || "",
        totalBedrooms: hotel.totalBedrooms || "",
        minPrice: hotel.priceRange.min || "",
        maxPrice: hotel.priceRange.max || "",
        washrooms: hotel.washrooms || "",
        facilities: hotel.facilities || [],
        email: hotel.email || "",
        priceRange: hotel.priceRange || "",
        latitude: hotel.location?.coordinates
          ? hotel.location.coordinates[1]
          : "",
        longitude: hotel.location?.coordinates
          ? hotel.location.coordinates[0]
          : "",
        extras: hotel.extras || "",
        payAtCheckout: hotel.payAtCheckout || false,
        hasRestaurant: hotel.hasRestaurant || false,
        images: [],
      });
      setExistingImages(hotel.images || []);
      setEnabled(hotel.payAtCheckout || false);

      if (hotel.contact) {
        const raw = hotel.contact.toString();
        let dialCode = "995";
        let localNumber = "";

        if (raw.startsWith("+")) {
          const digits = raw.slice(1).replace(/\D/g, "");
          let bestMatch = "";

          allCountries.forEach((c) => {
            if (
              digits.startsWith(c.dialCode) &&
              c.dialCode.length > bestMatch.length
            ) {
              bestMatch = c.dialCode;
            }
          });

          if (bestMatch) {
            dialCode = bestMatch;
            localNumber = digits.slice(bestMatch.length);
          } else {
            localNumber = digits;
          }
        }

        setContactCountryCode(dialCode);
        setContactLocalNumber(localNumber);
      }
    }
  }, [isEditing, hotel]);

  // Sync preview URLs with images array
  useEffect(() => {
    if (formData.images && formData.images.length > 0) {
      const currentImageCount = formData.images.length;
      const currentPreviewCount = imagePreviewUrls.length;
      
      // If preview URLs are missing for some images, create them
      if (currentPreviewCount < currentImageCount) {
        const missingUrls = [];
        for (let i = currentPreviewCount; i < currentImageCount; i++) {
          if (formData.images[i]) {
            missingUrls.push(URL.createObjectURL(formData.images[i]));
          }
        }
        if (missingUrls.length > 0) {
          setImagePreviewUrls((prev) => [...prev, ...missingUrls]);
        }
      }
    } else if (!formData.images || formData.images.length === 0) {
      // Clean up all preview URLs if no images
      imagePreviewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      if (imagePreviewUrls.length > 0) {
        setImagePreviewUrls([]);
      }
    }
  }, [formData.images?.length]);

  // Cleanup effect for preview URLs
  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviewUrls]);

  const { mutate: addHotelMutation, isPending } = useMutation({
    mutationFn: addHotel,
    onSuccess: () => {
      queryClient.invalidateQueries(["hotels"]);
      toast.success(t("addnewhotel").addHotelModal.success);
      onClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("addnewhotel").addHotelModal.error.addFailed;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateHotelMutation, isPending: updatingHotel } = useMutation({
    mutationFn: ({ id, hotelData }) => updateHotel(id, hotelData),
    onSuccess: () => {
      queryClient.invalidateQueries(["hotels"]);
      queryClient.invalidateQueries(["hotel", hotel._id]);
      toast.success(t("addnewhotel").addHotelModal.updateSuccess);
      onClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to update hotel. Please try again.";
      toast.error(errorMessage);
    },
  });
  

  const isValidInternationalPhone = (value) => {
    // Allow digits, spaces, "+", "-", and parentheses
    const phonePattern = /^[0-9+\-\s()]+$/;
    if (!phonePattern.test(value)) return false;

    const digitsOnly = value.replace(/\D/g, "");
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const buildFullContact = (countryCode, localNumber) => {
    const digitsOnly = (localNumber || "").replace(/\D/g, "");
    if (!digitsOnly) return "";
    return `+${countryCode}${digitsOnly}`;
  };

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.name)
      newErrors.name = t("addnewhotel").addHotelModal.errors.nameRequired;
    if (!data.category)
      newErrors.category =
        t("addnewhotel").addHotelModal.errors.categoryRequired;
    if (!data.rating)
      newErrors.rating = t("addnewhotel").addHotelModal.errors.ratingRequired;
    if (data.rating && (data.rating < 0 || data.rating > 5)) {
      newErrors.rating = t("addnewhotel").addHotelModal.errors.ratingRange;
    }
    if (!data.contact)
      newErrors.contact =
        t("addnewhotel").addHotelModal.errors.contactRequired;
    else if (data.contact && !isValidInternationalPhone(data.contact)) {
      newErrors.contact =
        t("addnewhotel").addHotelModal.errors.contactInvalid;
    }
    if (!data.address)
      newErrors.address = t("addnewhotel").addHotelModal.errors.addressRequired;
    if (!data.latitude)
      newErrors.latitude =
        t("addnewhotel").addHotelModal.errors.latitudeRequired;

    if (!data.longitude)
      newErrors.longitude =
        t("addnewhotel").addHotelModal.errors.longitudeRequired;
    if (!data.minPrice)
      newErrors.minPrice =
        t("addnewhotel").addHotelModal.errors.minPriceRequired;
    if (!data.maxPrice)
      newErrors.maxPrice =
        t("addnewhotel").addHotelModal.errors.maxPriceRequired;
    if (
      data.minPrice &&
      data.maxPrice &&
      parseFloat(data.minPrice) >= parseFloat(data.maxPrice)
    ) {
      newErrors.maxPrice =
        t("addnewhotel").addHotelModal.errors.maxPriceGreater;
    }
    if (!data.description)
      newErrors.description =
        t("addnewhotel").addHotelModal.errors.descriptionRequired;
    if (!data.totalBedrooms)
      newErrors.totalBedrooms =
        t("addnewhotel").addHotelModal.errors.totalBedroomsRequired;
    if (!data.washrooms)
      newErrors.washrooms =
        t("addnewhotel").addHotelModal.errors.washroomsRequired;
    if (!data.facilities)
      newErrors.facilities =
        t("addnewhotel").addHotelModal.errors.facilitiesRequired;
    if (!data.email)
      newErrors.email = t("addnewhotel").addHotelModal.errors.emailRequired;
    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = t("addnewhotel").addHotelModal.errors.emailValid;
    }

    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = t("addnewhotel").addHotelModal.errors.emailValid;
    }
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (name === "images") {
      const selectedFiles = Array.from(files);
      
      // Validate file types - only allow JPG, PNG, and JPEG
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidFiles = selectedFiles.filter(file => !allowedTypes.includes(file.type.toLowerCase()));
      
      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map(f => f.name).join(', ');
        const errorMessage = t("addnewhotel").addHotelModal.errors.invalidImageFormat.replace("{files}", invalidFileNames);
        toast.error(errorMessage);
        setErrors((prev) => ({ ...prev, images: t("addnewhotel").addHotelModal.errors.imageFormatOnly }));
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Clear error if all files are valid
      setErrors((prev) => ({ ...prev, images: "" }));

      setFormData((prev) => {
        const existingFiles = prev[name] || [];
        const combinedFiles = [...existingFiles, ...selectedFiles].slice(0, 10)
        return { ...prev, [name]: combinedFiles };
      });

      // Create preview URLs for new files
      const newPreviewUrls = selectedFiles.map(file => URL.createObjectURL(file));
      setImagePreviewUrls((prev) => [...prev, ...newPreviewUrls].slice(0, 10));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));

      const fieldError = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setContactCountryCode(newCode);

    const fullContact = buildFullContact(newCode, contactLocalNumber);
    setFormData((prev) => ({
      ...prev,
      contact: fullContact,
    }));

    const fieldError = fullContact
      ? validateField("contact", fullContact, formData)
      : t("addnewhotel").addHotelModal.errors.contactRequired;
    setErrors((prev) => ({ ...prev, contact: fieldError }));
  };

  const handleContactChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, "");

    setContactLocalNumber(numericValue);

    const fullContact = buildFullContact(contactCountryCode, numericValue);

    setFormData((prev) => ({
      ...prev,
      contact: fullContact,
    }));

    const fieldError = fullContact
      ? validateField("contact", fullContact, formData)
      : t("addnewhotel").addHotelModal.errors.contactRequired;
    setErrors((prev) => ({ ...prev, contact: fieldError }));
  };

  const handleDeleteImage = (index) => {
    // Revoke the object URL to prevent memory leaks
    const urlToRevoke = imagePreviewUrls[index];
    if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRevoke);
    }
    
    setFormData((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      
      // Update file input to reflect remaining files count
      if (fileInputRef.current && newImages.length > 0) {
        try {
          const dataTransfer = new DataTransfer();
          newImages.forEach((file) => {
            dataTransfer.items.add(file);
          });
          fileInputRef.current.files = dataTransfer.files;
        } catch (error) {
          // Fallback: reset input if DataTransfer is not supported
          fileInputRef.current.value = '';
        }
      } else if (fileInputRef.current && newImages.length === 0) {
        // If no images left, reset the input
        fileInputRef.current.value = '';
      }
      
      return { ...prev, images: newImages };
    });
    
    setImagePreviewUrls((prev) => {
      const newUrls = prev.filter((_, i) => i !== index);
      // Also revoke any URLs that might have been created inline
      return newUrls;
    });
  };

  const handleDeleteExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validateField = (name, value, data = {}) => {
    switch (name) {
      case "name":
        return value ? "" : "Hotel name is required.";
      case "category":
        return value ? "" : "Category is required.";
      case "rating":
        if (!value) return "Star rating is required.";
        if (value < 0 || value > 5)
          return "Star rating must be between 0 and 5.";
        return "";
      case "contact":
        if (!value)
          return t("addnewhotel").addHotelModal.errors.contactRequired;
        if (!isValidInternationalPhone(value)) {
          return t("addnewhotel").addHotelModal.errors.contactInvalid;
        }
        return "";
      case "address":
        return value ? "" : "Address is required.";
      case "minPrice":
        return value ? "" : "Minimum price is required.";
      case "maxPrice":
        if (!value) return "Maximum price is required.";
        if (data.minPrice && parseFloat(data.minPrice) >= parseFloat(value)) {
          return "Maximum price must be greater than minimum price.";
        }
        return "";
      case "totalBedrooms":
        return value ? "" : "Total bedrooms is required.";
      case "washrooms":
        return value ? "" : "Washroom type is required.";
      case "facilities":
        return value && value.length > 0 ? "" : "Facilities are required.";
      case "email":
        if (!value) return "Email is required.";
        if (!/\S+@\S+\.\S+/.test(value)) return "Please enter a valid email.";
        return "";
      case "description":
        return value ? "" : "Description is required.";
      case "location":
        return value ? "" : "Location is required.";
      default:
        return "";
    }
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setFormData((prev) => ({ ...prev, description: data }));

    const error = validateField("description", data);
    setErrors((prev) => ({ ...prev, description: error }));
  };

  const handleSelectChange = (selectedOptions, field) => {
    const values = selectedOptions.map((option) => option.value);

    setFormData((prev) => ({
      ...prev,
      [field]: values,
    }));

    const fieldError = validateField(field, values);
    setErrors((prev) => ({ ...prev, [field]: fieldError }));
  };

  const handleSubmit = () => {
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    if (!isEditing && (!formData.images || formData.images.length < 5)) {
      toast.error("Upload atleast 5 images");
      return;
    }

    const hotelData = {
      name: formData.name,
      category: formData.category,
      rating: Number(formData.rating),
      address: formData.address,
      description: formData.description,
      totalBedrooms: Number(formData.totalBedrooms),
      washrooms: formData.washrooms,
      facilities: formData.facilities || [],
      hasRestaurant: formData.hasRestaurant,
      extras: formData.extras,
      payAtCheckout: enabled,
      location: {
        type: "Point",
        coordinates: [
          parseFloat(formData.longitude),
          parseFloat(formData.latitude),
        ],
      },
      email: formData.email,
      contact: formData.contact,
      priceRange: {
        min: Number(formData.minPrice),
        max: Number(formData.maxPrice),
      },
    };

    const [lng, lat] = hotelData.location.coordinates;
    if (isNaN(lng) || isNaN(lat)) {
      toast.error("Invalid location coordinates");
      return;
    }

    const form = new FormData();
    form.append("name", hotelData.name);
    form.append("category", hotelData.category);
    form.append("rating", hotelData.rating);
    form.append("address", hotelData.address);
    form.append("description", hotelData.description);
    form.append("totalBedrooms", hotelData.totalBedrooms);
    form.append("washrooms", hotelData.washrooms);
    form.append("email", hotelData.email);
    form.append("contact", hotelData.contact);
    form.append("payAtCheckout", hotelData.payAtCheckout);
    form.append("hasRestaurant", hotelData.hasRestaurant);
    form.append("location", JSON.stringify(hotelData.location));
    form.append("priceRange", JSON.stringify(hotelData.priceRange));
    form.append("facilities", JSON.stringify(hotelData.facilities));
    form.append("extras", hotelData.extras);

    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => {
        form.append("images", file);
      });
    }

    if (isEditing) {
      updateHotelMutation({ id: hotel._id, hotelData: form });
    } else {
      addHotelMutation(form);
    }
  };

  // const facilitiesOptions = [
  //   { value: "Free Wi-Fi", label: "Free Wi-Fi" },
  //   { value: "Swimming Pool", label: "Swimming Pool" },
  //   { value: "Fitness Center", label: "Fitness Center" },
  //   { value: "Spa", label: "Spa" },
  //   { value: "Parking", label: "Parking" },
  //   { value: "Restaurant", label: "Restaurant" },
  //   { value: "Bar", label: "Bar" },
  //   { value: "Air Conditioning", label: "Air Conditioning" },
  //   { value: "Pets Allowed", label: "Pets Allowed" },
  // ];

  const amenities = t("home").searchFilter?.amenities || {};
  const facilitiesOptions = [
    { value: "wifi", label: amenities.wifi || "Free Wi-Fi" },
    { value: "Parking Available", label: amenities.parking || "Parking Available" },
    { value: "Outdoor Seating", label: amenities.outdoor_seating || "Outdoor Seating" },
    { value: "Live Music", label: amenities.live_music || "Live Music" },
    { value: "Pet Friendly", label: amenities.pet_friendly || "Pet Friendly" },
    { value: "Kid Friendly", label: amenities.kid_friendly || "Kid Friendly" },
    // { value: "vegan_options", label: amenities.vegan_options },
    { value: "Wheelchair Accessible", label: amenities.wheelchair_accessible || "Wheelchair Accessible" },
    { value: "Accepts Reservations", label: amenities.reservations || "Accepts Reservations" },
    { value: "Takeaway Available", label: amenities.takeaway || "Takeaway Available" },
    // { value: "delivery", label: amenities.delivery },
    // { value: "buffet", label: amenities.buffet },
    // { value: "bar", label: amenities.bar },
    // { value: "private_dining", label: amenities.private_dining },
    { value: "Smoking Area", label: amenities.smoking_area || "Smoking Area" },
    { value: "Swimming Pool", label: amenities.swimming_pool || "Swimming Pool" },
    { value: "Fitness Center", label: amenities.fitness_center || "Fitness Center" },
    { value: "Spa", label: amenities.spa || "Spa" },
    { value: "Air Conditioning", label: amenities.air_conditioning || "Air Conditioning" },
  ];

  return (
    <>
      <div className="w-full rounded-[20px] p-[30px] max-h-[90vh]">
        <div className="grid grid-col-1 sm:grid-cols-2 justify-between text-left pb-[21px] border-b border-[#EEEEEE] mb-[19px]">
          <h2 className="text-[22px] font-medium">
            {(isEditing || hotel)
              ? t("addnewhotel").addHotelModal.editTitle || "Edit Hotel"
              : t("addnewhotel").addHotelModal.title || "Add New Hotel"}
          </h2>
          <div className="flex items-center gap-[8px]">
            <h2 className="text-lg font-medium">
              {t("addnewhotel").addHotelModal.payatcheckout}
            </h2>
            <div
              onClick={() => setEnabled(!enabled)}
              className={`w-[60px] h-[31px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${enabled ? "bg-[#4CAF50]" : "bg-[#e1e1e1]"
                }`}
            >
              <div
                className={`bg-white w-[25px] h-[25px] rounded-full shadow-md transform transition-transform ${enabled ? "translate-x-7" : "translate-x-0"
                  }`}
              />
            </div>
          </div>
        </div>
        <div className=" overflow-y-auto max-h-[60vh]">
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.hotelName.label} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={
                  t("addnewhotel").addHotelModal.hotelName.placeholder
                }
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.name ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.category.label} *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.category ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              >
                <option value="" disabled selected>
                  {t("addnewhotel").addHotelModal.category.placeholder}
                </option>
                <option value="Luxury">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Luxury}
                </option>
                <option value="Budget">
                  {t("addnewhotel").addHotelModal.category.options.Budget}
                </option>
                <option value="Boutique">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Boutique}
                </option>
                <option value="Resort">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Resort}
                </option>
                <option value="Hostel">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Hostel}
                </option>
                <option value="Apartment">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Apartment}
                </option>
                <option value="Business">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Business}
                </option>
                <option value="Family">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Family}
                </option>
                <option value="Airport">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Airport}
                </option>
                <option value="Eco-friendly">
                  {" "}
                  {t("addnewhotel").addHotelModal.category.options.Eco_friendly}
                </option>
              </select>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category}</p>
              )}
            </div>
          </div>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.rating.label} *
              </label>
              <input
                type="number"
                name="rating"
                value={formData.rating}
                onChange={handleInputChange}
                min={0}
                max={5}
                placeholder={t("addnewhotel").addHotelModal.rating.placeholder}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.rating ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.rating && (
                <p className="text-red-500 text-sm mt-1">{errors.rating}</p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.contact.label} *
              </label>
              <div className="flex gap-2">
                <select
                  value={contactCountryCode}
                  onChange={handleCountryCodeChange}
                  className="w-[40%] md:w-[35%] lg:w-[30%] rounded-[10px] border border-[#DDDDDD] px-[10px] py-[12px] text-sm focus:outline-none focus:border-[#4CAF50]"
                >
                  {allCountries.map((c) => (
                    <option key={c.iso2} value={c.dialCode}>
                      +{c.dialCode} ({c.name})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="contact"
                  value={contactLocalNumber}
                  onChange={handleContactChange}
                  placeholder={t("addnewhotel").addHotelModal.contact.placeholder}
                  className={`flex-1 border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.contact ? "border-red-500" : "border-[#DDDDDD]"
                    }`}
                />
              </div>
              {errors.contact && (
                <p className="text-red-500 text-sm mt-1">{errors.contact}</p>
              )}
            </div>
          </div>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.bedrooms.label} *
              </label>
              <input
                type="number"
                name="totalBedrooms"
                value={formData.totalBedrooms}
                onChange={handleInputChange}
                placeholder={
                  t("addnewhotel").addHotelModal.bedrooms.placeholder
                }
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.totalBedrooms ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.totalBedrooms && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.totalBedrooms}
                </p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.washrooms.label} *
              </label>
              <select
                name="washrooms"
                value={formData.washrooms}
                onChange={handleInputChange}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.washrooms ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              >
                <option value="" disabled selected>
                  {t("addnewhotel").addHotelModal.washrooms.placeholder}
                </option>
                <option value="Attached">
                  {t("addnewhotel").addHotelModal.washrooms.options.Attached}
                </option>
                <option value="Separate">
                  {t("addnewhotel").addHotelModal.washrooms.options.Separate}
                </option>
                <option value="Common">
                  {t("addnewhotel").addHotelModal.washrooms.options.Common}
                </option>
                <option value="Private">
                  {t("addnewhotel").addHotelModal.washrooms.options.Private}
                </option>
                <option value="Shared">
                  {t("addnewhotel").addHotelModal.washrooms.options.Shared}
                </option>
                <option value="Ensuite">
                  {t("addnewhotel").addHotelModal.washrooms.options.Ensuite}
                </option>
                <option value="Family">
                  {t("addnewhotel").addHotelModal.washrooms.options.Family}
                </option>
                <option value="Accessible">
                  {t("addnewhotel").addHotelModal.washrooms.options.Accessible}
                </option>
                <option value="Disabled">
                  {t("addnewhotel").addHotelModal.washrooms.options.Disabled}
                </option>
                <option value="VIP">
                  {t("addnewhotel").addHotelModal.washrooms.options.VIP}
                </option>
                <option value="Public">
                  {t("addnewhotel").addHotelModal.washrooms.options.Public}
                </option>
                <option value="Luxury">
                  {t("addnewhotel").addHotelModal.washrooms.options.Luxury}
                </option>
              </select>
              {errors.washrooms && (
                <p className="text-red-500 text-sm mt-1">{errors.washrooms}</p>
              )}
            </div>
          </div>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.facilities.label} *
              </label>
              <Select
                isMulti
                options={facilitiesOptions}
                value={facilitiesOptions.filter((option) =>
                  formData.facilities.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  handleSelectChange(selectedOptions, "facilities")
                }
                className="border border-[#DDDDDD] rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50]"
              />
              {errors.facilities && (
                <p className="text-red-500 text-sm">{errors.facilities}</p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.extras.label} *
              </label>
              <input
                type="text"
                name="extras"
                value={formData.extras}
                onChange={handleInputChange}
                placeholder={t("addnewhotel").addHotelModal.extras.placeholder}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.extras ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.extras && (
                <p className="text-red-500 text-sm mt-1">{errors.extras}</p>
              )}
            </div>
          </div>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.location.label} *
              </label>
              <AddressAutocomplete
                onSelectAddress={(location) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: location.address,
                    latitude: location.latitude,
                    longitude: location.longitude,
                  }));

                  setErrors((prev) => ({
                    ...prev,
                    address: "",
                    latitude: "",
                    longitude: "",
                  }));
                }}
                selectedAddress={
                  formData.address && formData.latitude && formData.longitude
                    ? {
                        address: formData.address,
                        latitude: formData.latitude,
                        longitude: formData.longitude,
                      }
                    : null
                }
              />
              {errors.address && (
                <p className="text-red-500 text-sm mt-1">{errors.address}</p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.email.label} *
              </label>
              <input
                type="text"
                placeholder={t("addnewhotel").addHotelModal.email.placeholder}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.email ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          </div>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.priceRange.minPrice.label} *
              </label>
              <div className="flex gap-[16px]">
                <div className="w-full">
                  <input
                    type="number"
                    name="minPrice"
                    value={formData.minPrice}
                    onChange={handleInputChange}
                    placeholder={
                      t("addnewhotel").addHotelModal.priceRange.minPrice
                        .placeholder
                    }
                    className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.minPrice ? "border-red-500" : "border-[#DDDDDD]"
                      }`}
                  />
                  {errors.minPrice && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.minPrice}
                    </p>
                  )}
                </div>
                <div className="w-full">
                  <input
                    type="number"
                    name="maxPrice"
                    value={formData.maxPrice}
                    onChange={handleInputChange}
                    placeholder={
                      t("addnewhotel").addHotelModal.priceRange.maxPrice
                        .placeholder
                    }
                    className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.maxPrice ? "border-red-500" : "border-[#DDDDDD]"
                      }`}
                  />
                  {errors.maxPrice && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.maxPrice}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.restaurantInsideHotel.label}
              </label>
              <div className="flex gap-[16px]">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasRestaurant"
                    value="yes"
                    checked={formData.hasRestaurant === true}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, hasRestaurant: true }))
                    }
                    className="accent-[#4CAF50]"
                  />
                  {t("addnewhotel").addHotelModal.restaurantInsideHotel.yes}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasRestaurant"
                    value="no"
                    checked={formData.hasRestaurant === false}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, hasRestaurant: false }))
                    }
                    className="accent-[#4CAF50]"
                  />
                  {t("addnewhotel").addHotelModal.restaurantInsideHotel.no}
                </label>
              </div>
              {errors.hasRestaurant && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.hasRestaurant}
                </p>
              )}
            </div>
          </div>
          <div className="mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("addnewhotel").addHotelModal.description.label}
              </label>

              <CKEditor
                editor={ClassicEditor}
                data={formData.description}
                onChange={handleEditorChange}
                config={{
                  height: "300px",
                }}
                style={{ height: "300px" }}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description}
                </p>
              )}
            </div>
          </div>

          <div className="mb-[19px]">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("addnewhotel").addHotelModal.images.label}
            </label>
            
            <input
              ref={fileInputRef}
              type="file"
              name="images"
              accept="image/jpeg,image/jpg,image/png"
              multiple
              onChange={handleInputChange}
              className="block"
            />
            {errors.images && (
              <p className="text-red-500 text-sm mt-1">{errors.images}</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
              {(!formData.images || formData.images.length === 0) &&
                existingImages.map((url, idx) => (
                  <div
                    key={idx}
                    className="relative w-full h-24 rounded overflow-hidden border"
                  >
                    <img
                      src={`${url}`}
                      alt={`existing-${idx}`}
                      className="object-cover w-full h-full"
                    />
                    {isEditing && (
                      <button
                        onClick={() => handleDeleteExistingImage(idx)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 flex items-center justify-center"
                        title="Delete image"
                      >
                        <FiTrash size={14} />
                      </button>
                    )}
                  </div>
                ))}

              {formData.images &&
                formData.images.length > 0 &&
                Array.from(formData.images).map((file, idx) => (
                  <div
                    key={idx}
                    className="relative w-full h-24 rounded overflow-hidden border"
                  >
                    <img
                      src={imagePreviewUrls[idx] || ""}
                      alt={`preview-${idx}`}
                      className="object-cover w-full h-full"
                    />
                    <button
                      onClick={() => handleDeleteImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 flex items-center justify-center"
                      title="Delete image"
                    >
                      <FiTrash size={14} />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="flex gap-[30px] justify-end pt-[23px]">
          <button
            onClick={onClose}
            className="text-white font-bold inline-block py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
          >
            {t("addnewhotel").addHotelModal.buttons.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || updatingHotel}
            className="inline-block rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
          >
            {isPending || updatingHotel
              ? t("header").loading
              : isEditing
                ? t("addnewhotel").addHotelModal.buttons.update
                : t("addnewhotel").addHotelModal.buttons.add}
          </button>
        </div>
      </div>
    </>
  );
}

export default AddHotelModel;
